if ( typeof ToggleTab !== 'function' ) {

	class ToggleTab extends HTMLElement {

		constructor(){
			super();
			this._prefix = window.KT_PREFIX || '';

			this.titleEl = this.querySelector('[data-js-title]');
			this.contentEl = this.querySelector('[data-js-content]');
			this.titleEl.innerHTML = `${this.titleEl.innerHTML}${this.hasAttribute('data-togglepack-alternate') ? KROWN.settings.symbols.toggle_pack_alternate : KROWN.settings.symbols.toggle_pack}`;

			if ( this.hasAttribute('data-toggle-inert') ) {
				this.toggleInert = true;
			}

			this.titleEl.addEventListener('click',this.onClickHandler.bind(this));
			this.titleEl.addEventListener('keydown', e=>{
				if ( e.keyCode == window.KEYCODES.RETURN ) {
					this.onClickHandler();
				}
			})

		}
		
		/* Distance from the top of the viewport to the bottom of whatever is
		   pinned there, so a title is never left sitting behind it. */
		stickyOffset(){
			let bottom = 0;
			document.querySelectorAll('.sticky-header, [data-js-sticky-header]').forEach(el => {
				const style = getComputedStyle(el);
				if ( style.position !== 'fixed' && style.position !== 'sticky' ) return;
				if ( style.visibility === 'hidden' || style.display === 'none' ) return;
				const rect = el.getBoundingClientRect();
				// Only count it when it is actually over the top of the page; this
				// header slides out of view on scroll down (top: -70px).
				if ( rect.bottom > bottom && rect.top <= 0 ) bottom = rect.bottom;
			});
			return bottom;
		}

		/* Live behaviour, measured on the Flatsome accordion at
		   originmattress.com.tw (375px): opening a panel scrolls that panel's
		   title to the top of the viewport -- title top went 360 -> 0, 378 -> 0,
		   scrolling either direction to get there. Closing one does not scroll.

		   Ours lands it just under the sticky header instead of hard against 0,
		   because this theme's header re-appears on an upward scroll and would
		   otherwise cover the question. When the header is hidden the offset is 0
		   and the result is identical to the reference.

		   Waits for the 200ms slide first: opening also collapses the previously
		   open panel, which reflows everything below it, so measuring before that
		   settles would scroll to the wrong place. */
		/* Full height of the pinned header, whether or not it is on screen right
		   now. offsetHeight still reports it while it is translated out of view. */
		headerHeight(){
			let h = 0;
			document.querySelectorAll('.sticky-header, [data-js-sticky-header]').forEach(el => {
				const style = getComputedStyle(el);
				if ( style.position !== 'fixed' && style.position !== 'sticky' ) return;
				if ( style.display === 'none' ) return;
				h = Math.max(h, el.offsetHeight);
			});
			return h;
		}

		scrollTitleToTop(){
			/* Live behaviour, measured on originmattress.com.tw at 375px: opening a
			   panel scrolls its title to the top of the viewport (360 -> 0, 378 -> 0),
			   in whichever direction that takes. Closing does not scroll.

			   The target here is direction-aware, which the reference does not need.
			   This theme's header hides on a downward scroll and slides back in on an
			   upward one, so:

			     scrolling DOWN  -> header stays hidden, aim for 0, same as the live site
			     scrolling UP    -> header will re-appear, aim for its full height

			   Reading the header's CURRENT position instead was the first attempt and
			   it chased its own tail: correcting upward revealed the header, which
			   moved the target, which needed another correction. Measured failure --
			   title parked at 0 with the header down at 70 covering it. Deciding from
			   the direction of travel settles in one move.

			   Nothing here touches the header: no classes, no styles, no listeners.
			   It only measures and scrolls the window. */
			/* Always leave the header's full height clear, whichever way the page
			   travels to get there.

			   The earlier version aimed for 0 when scrolling down, on the grounds
			   that the header is hidden at that moment. It is -- but only until
			   the shopper scrolls up again to read the answer, at which point the
			   header slides back in over the question they just opened. Reserving
			   the space up front is the only target that stays correct no matter
			   what they do next.

			   The live site does not need this: its header is pinned at top: 0
			   permanently (measured: stepped down, stepped up, back to the top --
			   top stayed 0 throughout, never hides), so content there is always
			   laid out below it. Ours hides and re-shows, so the room has to be
			   reserved deliberately. */
			const targetFor = () => this.headerHeight();

			/* Instant, not smooth. A smooth scroll starting while the 200ms slide
			   and the sibling collapse are still settling kept getting cut short
			   -- measured landing at 421 (i.e. not moved at all) on 3 of 5 opens,
			   and 25 on another. An instant scroll cannot be interrupted, and the
			   passes below catch anything that shifts afterwards. */
			const place = () => {
				const delta = this.titleEl.getBoundingClientRect().top - targetFor();
				if ( Math.abs(delta) >= 2 ) {
					window.scrollBy({ top: delta, left: 0, behavior: 'instant' });
				}
			};

			setTimeout(() => {
				place();
				// Re-run after the panel has fully settled, then once more after the
				// header transition, and finally make sure nothing is covering it.
				setTimeout(place, 160);
				setTimeout(() => {
					place();
					const rect = this.titleEl.getBoundingClientRect();
					const floor = this.stickyOffset();
					if ( rect.top < floor - 1 ) {
						window.scrollBy({ top: rect.top - floor, left: 0, behavior: 'instant' });
					}
				}, 480);
			}, 240);
		}

		onClickHandler(){

	// 👉 CLOSE ALL OTHER TOGGLES FIRST (GLOBAL FIX)
	const prefix = this._prefix;
	document.querySelectorAll(`toggle-tab.${prefix}opened`).forEach(opened => {
		if (opened !== this) {
			opened.classList.remove(`${prefix}opened`);
			
			const title = opened.querySelector('[data-js-title]');
			const content = opened.querySelector('[data-js-content]');
			
			title.setAttribute('aria-expanded', 'false');
			opened.slideUp(content, 200);

			if (opened.toggleInert) {
				content.setAttribute('inert', '');
			}
		}
	});

	// 👉 NOW NORMAL TOGGLE BEHAVIOR
	if ( !this.classList.contains(`${prefix}opened`) ) {

		this.classList.add(`${prefix}opened`);
		this.titleEl.setAttribute('aria-expanded', 'true');
		this.slideDown(this.contentEl, 200);

		if ( this.toggleInert ) {
			this.contentEl.removeAttribute('inert');
		}

		setTimeout(()=>{
			this.contentEl.querySelector('css-slider')?.resetSlider();
		}, 200);

	} else {

		this.classList.remove(`${prefix}opened`);
		this.titleEl.setAttribute('aria-expanded', 'false');

		if ( this.toggleInert ) {
			this.contentEl.setAttribute('inert', '');
		}

		this.slideUp(this.contentEl, 200);
	}

	// Only on the way open, matching the live site: closing a panel leaves the
	// page where it is.
	if ( this.classList.contains(`${prefix}opened`) ) {
		this.scrollTitleToTop();
	}
}

		slideUp(target, duration){
			target.style.transitionProperty = 'height, margin, padding';
			target.style.transitionDuration = duration + 'ms';
			target.style.boxSizing = 'border-box';
			target.style.height = target.offsetHeight + 'px';
			target.offsetHeight;
			target.style.overflow = 'hidden';
			target.style.height = 0;
			target.style.paddingTop = 0;
			target.style.paddingBottom = 0;
			target.style.marginTop = 0;
			target.style.marginBottom = 0;
			setTimeout(()=>{
				target.style.display = 'none';
				target.style.removeProperty('height');
				target.style.removeProperty('padding-top');
				target.style.removeProperty('padding-bottom');
				target.style.removeProperty('margin-top');
				target.style.removeProperty('margin-bottom');
				target.style.removeProperty('overflow');
				target.style.removeProperty('transition-duration');
				target.style.removeProperty('transition-property');
			}, duration);
		}
		slideDown(target, duration) {
			target.style.removeProperty('display');
			var display = window.getComputedStyle(target).display;

			if (display === 'none')
				display = 'block';

			target.style.display = display;
			var height = target.offsetHeight;
			target.style.overflow = 'hidden';
			target.style.height = 0;
			target.style.paddingTop = 0;
			target.style.paddingBottom = 0;
			target.style.marginTop = 0;
			target.style.marginBottom = 0;
			target.offsetHeight;
			target.style.boxSizing = 'border-box';
			target.style.transitionProperty = "height, margin, padding";
			target.style.transitionDuration = duration + 'ms';
			target.style.height = height + 'px';
			target.style.removeProperty('padding-top');
			target.style.removeProperty('padding-bottom');
			target.style.removeProperty('margin-top');
			target.style.removeProperty('margin-bottom');
			setTimeout(()=>{
				target.style.removeProperty('height');
				target.style.removeProperty('overflow');
				target.style.removeProperty('transition-duration');
				target.style.removeProperty('transition-property');
			}, duration);
		}

	}

  if ( typeof customElements.get('toggle-tab') == 'undefined' ) {
		customElements.define('toggle-tab', ToggleTab);
	}

}

if ( typeof ToggleGroup !== 'function' ) {

	class ToggleGroup extends HTMLElement {

		constructor(){
			super();
			this._prefix = window.KT_PREFIX || '';
		}

		connectedCallback() {
			this.addEventListener('click', (e) => {

				const title = e.target.closest('[data-js-title]');
				if (!title) return;

				const currentToggle = title.closest('toggle-tab');
				if (!currentToggle) return;

				// Close all others FIRST
				this.querySelectorAll(`toggle-tab.${this._prefix}opened`).forEach(opened => {
					if (opened !== currentToggle) {
						opened.onClickHandler(); // clean close
					}
				});

			});
		}

	}

	if ( typeof customElements.get('toggle-group') == 'undefined' ) {
		customElements.define('toggle-group', ToggleGroup);
	}

}